import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { LuCheck } from "react-icons/lu";
import { FiUserPlus, FiX } from "react-icons/fi";

import ListPageShell from "../list-page/ListPageShell";
import ListPageHeader from "../list-page/ListPageHeader";
import FormSelect from "../FormSelect";

import { openModalWithTimeout } from "../../store/alertSlice";
import { useCreateUserMutation } from "../../store/services/userServices";
import { getAvailableRolesForCreation } from "../../utils/roleUtils";
import { useGetRolesQuery } from "../../store/services/rolesServices";

const labelClass = "block text-xs font-semibold text-gray-700 mb-1.5";
const inputClass =
  "w-full rounded-lg border bg-white px-3 py-2.5 text-sm shadow-sm transition placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--secondary-color)]/30 focus:border-[var(--secondary-color)] disabled:bg-gray-50 disabled:text-gray-400";

const fieldClass = (hasError) =>
  `${inputClass} ${hasError ? "border-red-400" : "border-gray-300"}`;

export default function AddUser() {
  const [createUser, { isLoading: isSubmitting }] = useCreateUserMutation();
  const { data: rolesData } = useGetRolesQuery();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [userDetails, setUserDetails] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    personal_number: "",
    birthdate: "",
    role: "",
  });
  const [errors, setErrors] = useState({});

  const getAvailableRoles = () => {
    if (!rolesData?.data) return [];
    return getAvailableRolesForCreation(rolesData.data).map((role) => ({
      value: role.id,
      label:
        role.display_name ||
        role.name
          .replace(/_/g, " ")
          .toLowerCase()
          .replace(/^\w/, (c) => c.toUpperCase()),
    }));
  };

  /* Default role once roles arrive */
  useEffect(() => {
    if (rolesData?.data && !userDetails.role) {
      const available = getAvailableRolesForCreation(rolesData.data);
      if (available.length > 0) {
        setUserDetails((prev) => ({ ...prev, role: available[0].id }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolesData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "personal_number") {
      const onlyNumbers = value.replace(/\D/g, "");
      setUserDetails((p) => ({ ...p, personal_number: onlyNumbers }));
    } else {
      setUserDetails((p) => ({ ...p, [name]: value }));
    }
    if (value) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validate = () => {
    const e = {};
    if (!userDetails.first_name) e.first_name = "Emri është i detyrueshëm";
    if (!userDetails.last_name) e.last_name = "Mbiemri është i detyrueshëm";
    if (!userDetails.email) e.email = "Email-i është i detyrueshëm";
    if (!userDetails.phone_number) e.phone_number = "Numri i telefonit është i detyrueshëm";
    if (!userDetails.personal_number) {
      e.personal_number = "Numri personal është i detyrueshëm";
    } else if (userDetails.personal_number.length < 10) {
      e.personal_number = "Numri personal duhet të ketë 10 shifra";
    }
    if (!userDetails.birthdate) e.birthdate = "Ditëlindja është e detyrueshme";

    setErrors(e);
    if (Object.keys(e).length > 0) {
      const first = Object.values(e)[0];
      dispatch(openModalWithTimeout({ text: first, type: "error" }, 3000));
    }
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      await createUser({
        first_name: userDetails.first_name,
        last_name: userDetails.last_name,
        email: userDetails.email,
        phone_number: userDetails.phone_number,
        personal_number: userDetails.personal_number,
        birthdate: userDetails.birthdate,
        role: userDetails.role,
      }).unwrap();
      dispatch(
        openModalWithTimeout(
          {
            text: `Përdoruesi u krijua me sukses. Linku për vendosjen e fjalëkalimit u dërgua në ${userDetails.email}`,
            type: "success",
          },
          5000
        )
      );
      navigate("/atk_users");
    } catch (error) {
      const msg =
        error?.data?.error ||
        error?.data?.message ||
        "Krijimi i përdoruesit dështoi";
      dispatch(openModalWithTimeout({ text: msg, type: "error" }, 3000));
    }
  };

  const handleCancel = () => navigate("/atk_users");

  const availableRoles = getAvailableRoles();

  return (
    <ListPageShell>
      <ListPageHeader
        eyebrow={{ icon: <FiUserPlus size={18} />, label: "Përdoruesit" }}
        title="Krijo përdorues të ri"
        subtitle="Plotëso të dhënat dhe zgjidh rolin e përdoruesit."
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition"
            >
              <FiX size={16} />
              Anulo
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 rounded-xl bg-[var(--secondary-color)] hover:bg-[var(--secondary-color)]/90 active:scale-[0.98] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <LuCheck size={18} />
              {isSubmitting ? "Po ruhet..." : "Ruaj"}
            </button>
          </div>
        }
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.03)] p-5 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="first_name" className={labelClass}>Emri</label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={userDetails.first_name}
                onChange={handleChange}
                className={fieldClass(errors.first_name)}
                placeholder="p.sh. Filan"
              />
              {errors.first_name && (
                <p className="mt-1 text-xs text-red-600">{errors.first_name}</p>
              )}
            </div>
            <div>
              <label htmlFor="last_name" className={labelClass}>Mbiemri</label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={userDetails.last_name}
                onChange={handleChange}
                className={fieldClass(errors.last_name)}
                placeholder="p.sh. Fisteku"
              />
              {errors.last_name && (
                <p className="mt-1 text-xs text-red-600">{errors.last_name}</p>
              )}
            </div>
            <div>
              <label htmlFor="personal_number" className={labelClass}>
                Numri personal
              </label>
              <input
                type="text"
                id="personal_number"
                name="personal_number"
                value={userDetails.personal_number}
                onChange={handleChange}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="10"
                className={`${fieldClass(errors.personal_number)} tabular-nums`}
                placeholder="10 shifra"
              />
              {errors.personal_number && (
                <p className="mt-1 text-xs text-red-600">{errors.personal_number}</p>
              )}
            </div>
            <div>
              <label htmlFor="birthdate" className={labelClass}>Ditëlindja</label>
              <input
                type="date"
                id="birthdate"
                name="birthdate"
                value={userDetails.birthdate}
                onChange={handleChange}
                className={fieldClass(errors.birthdate)}
              />
              {errors.birthdate && (
                <p className="mt-1 text-xs text-red-600">{errors.birthdate}</p>
              )}
            </div>
            <div>
              <label htmlFor="email" className={labelClass}>Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={userDetails.email}
                onChange={handleChange}
                className={fieldClass(errors.email)}
                placeholder="[email protected]"
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-600">{errors.email}</p>
              )}
            </div>
            <div>
              <label htmlFor="phone_number" className={labelClass}>
                Numri i telefonit
              </label>
              <input
                type="tel"
                id="phone_number"
                name="phone_number"
                value={userDetails.phone_number}
                onChange={handleChange}
                className={fieldClass(errors.phone_number)}
                placeholder="+383 ..."
              />
              {errors.phone_number && (
                <p className="mt-1 text-xs text-red-600">{errors.phone_number}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <label htmlFor="role" className={labelClass}>Roli</label>
              <FormSelect
                inputId="role"
                isSearchable={false}
                placeholder="— pa role të disponueshme —"
                options={availableRoles}
                value={availableRoles.find((r) => r.value === userDetails.role) || null}
                onChange={(selected) =>
                  setUserDetails((p) => ({ ...p, role: selected?.value ?? "" }))
                }
              />
            </div>
            {/* The backend emails the new user a 24h link to /set-password, so no password is chosen here */}
            <p className="md:col-span-2 rounded-lg bg-gray-50 px-3 py-2.5 text-xs text-gray-600 ring-1 ring-inset ring-gray-200">
              Përdoruesi do të marrë një email me linkun për të vendosur fjalëkalimin. Linku është i vlefshëm për 24 orë.
            </p>
          </div>
        </div>

        {/* Hidden submit so Enter triggers save */}
        <button type="submit" className="hidden" />
      </form>
    </ListPageShell>
  );
}
